import { useMemo } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import colors from 'config/colors';
import {
  ActionButtonItem,
  ActionButtonOrder,
  HasUnacceptChangesLabel,
} from 'modules/ChefScene/components/ActionButton';
import {
  ReprintButtonItem,
  ReprintButtonOrder,
} from 'modules/ChefScene/components/ActionButton/ReprintButton';
import { RouteNames } from 'navigation/linkingConfig';
import { useAppSelector } from 'store/hooks';
import { NotificationsSelectors } from 'store/Notifications';
import {
  ItemPreparationStatuses,
  OrderInternalStatuses,
} from 'store/Orders/enums';
import {
  checkIfScheduledOrder,
  getOrderInternalStatus,
} from 'store/Orders/utils';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { AnalyticsEvents, useLogEventCallback } from 'util/analytics';

import type { Order, OrderItem } from 'store/Orders/types';
import type { IonIconName } from 'types/icons';

type StatusButtonsProps = {
  order: Order;
  item: OrderItem;
  orderInternalStatus: OrderInternalStatuses;
  hasUnacceptedNotifications?: boolean;
  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;
  onPressedAction?: () => void;
  actionButtonArrangement?: 'vertical' | 'horizontal';
  fullWidth?: boolean;
};

export const StatusButtons = ({
  order,
  item,
  orderInternalStatus,
  onStatusChanged,
  hasUnacceptedNotifications,
  onPressedAction,
  fullWidth,
  actionButtonArrangement,
}: StatusButtonsProps) => {
  const orderDetailsInterfaceSettings = useAppSelector(
    SettingSelectors.selectOrderDetailsInterfaceSettings,
  );
  const featureFlags = useAppSelector(UserSelectors.selectFeatureFlags);

  const isScheduledFutureOrder = checkIfScheduledOrder(order);

  const logEvent = useLogEventCallback();

  const { color, iconName } = useMemo((): {
    color?: string;
    iconName?: IonIconName;
  } => {
    switch (item.status) {
      case ItemPreparationStatuses.BEING_PREPARED:
        return {
          color: '#e98400',
          iconName: 'flame',
        };
      case ItemPreparationStatuses.PREPARED:
      case ItemPreparationStatuses.COMPLETED:
        return {
          iconName: 'checkmark-circle',
          color: '#6db66b',
        };
      default:
        return {};
    }
  }, [item]);

  const getStatusChangeCallback =
    (params?: Dict) =>
    (
      statusToUpdate: ItemPreparationStatuses,
      duration: number,
      startDate: Date,
      endDate: Date,
    ) => {
      onPressedAction?.();
      logEvent(
        AnalyticsEvents.itemStatusChanged({
          isScheduled: isScheduledFutureOrder,
          screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
          status: statusToUpdate,
          itemId: item.uuid,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          executionTime: duration,
          ...params,
        }),
      );

      onStatusChanged?.({
        mode: 'single',
        startDate,
        endDate,
        status: statusToUpdate,
        itemId: item.uuid,
        orderId: item.order_uuid,
        screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
      });
    };

  const interfaceSettingsFeatureFlag = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );
  return (
    <View>
      {orderInternalStatus ===
      OrderInternalStatuses.CANCELLED ? null : hasUnacceptedNotifications ? (
        <HasUnacceptChangesLabel fullWidth={fullWidth} />
      ) : orderDetailsInterfaceSettings.shouldDisplayActionButtonItems ? (
        !item.cookable ? null : item.status !==
          ItemPreparationStatuses.PREPARED ? (
          <ActionButtonItem
            arrangement={actionButtonArrangement}
            includePrintButton
            order={order}
            item={item}
            isScheduled={isScheduledFutureOrder}
            onPressFinish={getStatusChangeCallback({ print: false })}
            onPressFinishAndPrint={getStatusChangeCallback({ print: true })}
          />
        ) : item.status === ItemPreparationStatuses.PREPARED &&
          interfaceSettingsFeatureFlag.action_button_item.reprint ? (
          <ReprintButtonItem
            itemUUID={item.uuid}
            onPress={() => {
              logEvent(
                AnalyticsEvents.reprintButtonPressed({
                  screen:
                    RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
                }),
              );
            }}
          />
        ) : null
      ) : iconName ? (
        <Ionicons name={iconName} size={40} color={color} />
      ) : null}
    </View>
  );
};

type NonCookableActionButtonProps = {
  order: Order;
  items: OrderItem[];
  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;
};

export function NonCookableActionButton({
  order,
  items,
  onStatusChanged,
}: NonCookableActionButtonProps) {
  const logEvent = useLogEventCallback();

  const interfaceSettings = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const getStatusChangeCallback =
    (params?: Dict) =>
    (
      statusToUpdate: ItemPreparationStatuses,
      itemsToUpdate: OrderItem[],
      duration: number,
      startDate: Date,
      endDate: Date,
    ) => {
      const isScheduled = checkIfScheduledOrder(order);

      logEvent(
        AnalyticsEvents.itemStatusChanged({
          isScheduled: isScheduled,
          screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
          status: statusToUpdate,
          orderId: order.uuid,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          executionTime: duration,
          ...params,
        }),
      );

      for (const item of itemsToUpdate) {
        onStatusChanged?.({
          mode: 'group',
          startDate,
          endDate,
          status: statusToUpdate,
          itemId: item.uuid,
          orderId: item.order_uuid,
          screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
        });
      }
    };

  const nonCookableItems = useMemo(() => {
    return items.filter((item) => !item.cookable);
  }, [items]);

  const orderInternalStatus = useMemo(() => {
    return getOrderInternalStatus(order, nonCookableItems);
  }, [nonCookableItems, order]);

  const hasUnacceptedNotifications = useAppSelector(
    (state) =>
      NotificationsSelectors.selectUnattendedForItems(state, nonCookableItems)
        .length > 0,
  );

  const actionButtonsInterfaceSettings = useAppSelector(
    SettingSelectors.selectActionButtonsInterfaceSettings,
  );

  return (
    <View
      style={{
        backgroundColor: colors.darkGray,
        overflow: 'hidden',
        zIndex: 100,
      }}
    >
      {hasUnacceptedNotifications ? (
        <HasUnacceptChangesLabel color="white" style={{ paddingLeft: 8 }} />
      ) : orderInternalStatus ===
        OrderInternalStatuses.CANCELLED ? null : nonCookableItems.every(
          (item) => item.status !== ItemPreparationStatuses.PREPARED,
        ) ? (
        // NOTE: some broken legacy code that need override
        <View style={{ height: 76 }}>
          <ActionButtonOrder
            isNonCookable={true}
            includePrintButton
            order={order}
            items={nonCookableItems}
            isScheduled={checkIfScheduledOrder(order)}
            onPressFinish={getStatusChangeCallback({ print: false })}
            onPressFinishAndPrint={getStatusChangeCallback({
              print: true,
            })}
          />
        </View>
      ) : interfaceSettings.action_button_noncookables_section.reprint &&
        actionButtonsInterfaceSettings.shouldDisplayAllActionButton ? (
        <ReprintButtonOrder
          order={order}
          itemUUIDs={nonCookableItems.map((item) => item.uuid)}
          onPress={() => {
            logEvent(
              AnalyticsEvents.reprintButtonPressed({
                screen: RouteNames.Home.Main.Drawer.Tabs.Overview.OrderDetails,
              }),
            );
          }}
        />
      ) : null}
    </View>
  );
}
